import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DISCOVERY_STATUS,
  getDiscoveryProgress,
} from "../../../Services/journey";
import "../style.css";
import { useStandaloneHeader, JourneyBackHeader, JourneySubpageCard } from "../shared";
import DomainAssessmentView from "../../../Components/YourElements/DomainAssessmentView";
import "../../../Components/YourElements/styles.css";
import "./style.css";

/**
 * Your Discoveries (Figma 9452:4020) + Begin Discovery modal (9452:4629).
 *
 * One call — `GET /ARIS/v4/journey/discovery-progress` — renders the five
 * rows. `resolved: false` means the read failed server-side: render a retry
 * card, never an empty list. Only an AVAILABLE row opens the Begin modal and
 * routes to the runner; COMPLETED rows open their domain; LOCKED/EMERGES rows
 * never navigate (EMERGES is derived — it has no runner).
 */

const DOMAIN_TILE_BG = {
  IDENTITY: "#e7f0ff",
  COGNITION: "#efeaff",
  RESOLUTION: "#feeceb",
  MOTIVES: "#ffe9f3",
  JUDGMENT: "#e6fbf8",
};

const ClockIcon = () => (
  <svg
    className="disc-glyph"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M8 4.8V8l2.2 1.4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const LockIcon = () => (
  <svg
    className="disc-glyph"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect
      x="3.25"
      y="7.25"
      width="9.5"
      height="6"
      rx="1.5"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M5.5 7V5.5a2.5 2.5 0 0 1 5 0V7"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </svg>
);

const CheckIcon = () => (
  <svg
    className="disc-glyph"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="8" cy="8" r="7" fill="currentColor" />
    <path
      d="M5 8.2l2 2L11 6"
      stroke="#fff"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** icon can arrive as a URL or as a short glyph/emoji; degrade to a spark. */
export const DiscoveryIcon = ({ icon, title }) => {
  if (icon && /^https?:\/\//.test(icon)) {
    return <img className="disc-tile__img" src={icon} alt={title || ""} />;
  }
  return (
    <span role="img" aria-label="">
      {icon || "✦"}
    </span>
  );
};

export const formatDuration = (item) => {
  if (item?.duration) return item.duration;
  if (item?.estimated_minutes) return `${item.estimated_minutes} min`;
  return "";
};

const StatusSlot = ({ status }) => {
  switch (status) {
    case DISCOVERY_STATUS.COMPLETED:
      return (
        <span className="disc-status disc-status--done">
          <CheckIcon /> Completed
        </span>
      );
    case DISCOVERY_STATUS.AVAILABLE:
      return (
        <span className="disc-status disc-status--next">
          Next Up <span aria-hidden="true">›</span>
        </span>
      );
    case DISCOVERY_STATUS.LOCKED:
      return (
        <span className="disc-status disc-status--locked">
          <LockIcon /> Locked
        </span>
      );
    case DISCOVERY_STATUS.EMERGES:
      return <span className="disc-status disc-status--locked">Emerges</span>;
    default:
      return null;
  }
};

const DiscoveryRow = ({ item, onOpen }) => {
  const status = item.status;
  const emerges = status === DISCOVERY_STATUS.EMERGES;
  const dimmed = status === DISCOVERY_STATUS.LOCKED || emerges;
  const clickable =
    status === DISCOVERY_STATUS.AVAILABLE ||
    status === DISCOVERY_STATUS.COMPLETED;

  const handleOpen = () => {
    if (clickable) onOpen(item);
  };

  const className =
    "journey-card disc-row" +
    (dimmed ? " disc-row--dimmed" : "") +
    (status === DISCOVERY_STATUS.AVAILABLE ? " disc-row--next" : "");

  return (
    <div
      className={className}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={handleOpen}
      onKeyDown={(event) => {
        if (clickable && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          handleOpen();
        }
      }}
    >
      <div className="disc-row__head">
        <span
          className="journey-list__tile disc-row__tile"
          style={{ background: DOMAIN_TILE_BG[item.domain] || "#eef2f6" }}
        >
          <DiscoveryIcon icon={item.icon} title={item.title} />
        </span>
        <div className="journey-list__text">
          <p className="journey-list__label">{item.title}</p>
          {item.description && (
            <p className="journey-list__sub">{item.description}</p>
          )}
        </div>
      </div>
      <hr className="journey-divider disc-row__divider" />
      <div className="disc-row__foot">
        {emerges ? (
          <p className="disc-row__note">
            Emerges from your other discoveries
          </p>
        ) : (
          <span className="disc-row__duration">
            <ClockIcon /> {formatDuration(item)}
          </span>
        )}
        <StatusSlot status={status} />
      </div>
    </div>
  );
};

/** Begin Discovery sheet (9452:4629) — local overlay; GenericModal's fixed
 * header/footer chrome doesn't fit this centered-hero layout. */
const BeginDiscoveryModal = ({ item, onClose }) => {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!item) return null;
  return (
    <div className="disc-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="disc-modal"
        role="dialog"
        aria-modal="true"
        aria-label={item.title}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="disc-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>
        <span
          className="disc-modal__orb"
          style={{ background: DOMAIN_TILE_BG[item.domain] || "#eef2f6" }}
        >
          <DiscoveryIcon icon={item.icon} title={item.title} />
        </span>
        <h2 className="disc-modal__title">{item.title}</h2>
        {item.description && (
          <p className="disc-modal__desc">{item.description}</p>
        )}
        <p className="disc-modal__duration">
          <ClockIcon /> {formatDuration(item)}
        </p>
        <DomainAssessmentView item={{ title: item.title }} />
      </div>
    </div>
  );
};

function DiscoveriesScreen() {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState(null);

  useStandaloneHeader();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    (async () => {
      try {
        const payload = await getDiscoveryProgress();
        if (!cancelled) setProgress(payload || null);
      } catch (_) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const unresolved =
    !loading && !error && (!progress || progress.resolved === false);
  const items = [...(progress?.discoveries || [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );

  const handleOpen = (item) => {
    if (item.status === DISCOVERY_STATUS.COMPLETED) {
      navigate("/elements");
      return;
    }
    if (item.status === DISCOVERY_STATUS.AVAILABLE) {
      // Derived discoveries (EMERGES) never reach here — and even a derived
      // row mislabelled AVAILABLE must never open the runner.
      if (item.is_derived) return;
      setSelected(item);
    }
  };

  return (
    <div className="journey-page">
      <div className="journey-page__inner">
        <JourneyBackHeader to="/journey" title="Your Discoveries" />
        <JourneySubpageCard>
          <p className="disc-subtitle">
            Your understanding is becoming more complete with every Discovery.
          </p>
          {loading && (
            <div className="journey-card journey-card--status">
              Loading your discoveries…
            </div>
          )}

          {!loading && (error || unresolved) && (
            <div className="journey-card journey-card--status">
              <p className="disc-retry__text">
                We couldn't load your discoveries just now.
              </p>
              <button
                type="button"
                className="journey-button disc-retry__button"
                onClick={() => setReloadKey((key) => key + 1)}
              >
                Try again
              </button>
            </div>
          )}

          {!loading && !error && !unresolved &&
            items.map((item) => (
              <DiscoveryRow
                key={item.discovery_key}
                item={item}
                onOpen={handleOpen}
              />
            ))}
        </JourneySubpageCard>
      </div>

      <BeginDiscoveryModal item={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

export default DiscoveriesScreen;
